import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { API } from "@/config";

const emptyQuestion = () => ({ key: crypto.randomUUID(), question: "", options: ["", "", "", ""], correct_index: 0 });

export const ExamsSection = () => {
  const [tab, setTab] = useState("exams");
  const [exams, setExams] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", duration_minutes: 10, passing_percent: 40 });
  const [questions, setQuestions] = useState([emptyQuestion()]);

  const fetchExams = async () => {
    const { data } = await axios.get(`${API}/exams/all`, { withCredentials: true });
    setExams(data);
  };
  const fetchAttempts = async () => {
    const { data } = await axios.get(`${API}/attempts`, { withCredentials: true });
    setAttempts(data);
  };

  useEffect(() => {
    fetchExams();
    fetchAttempts();
  }, []);

  const updateQ = (i, key, value) => {
    const next = [...questions];
    next[i] = { ...next[i], [key]: value };
    setQuestions(next);
  };

  const updateOption = (qi, oi, value) => {
    const next = [...questions];
    const opts = [...next[qi].options];
    opts[oi] = value;
    next[qi] = { ...next[qi], options: opts };
    setQuestions(next);
  };

  const createExam = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API}/exams`,
        { ...form, questions: questions.filter((q) => q.question.trim()) },
        { withCredentials: true }
      );
      toast.success("Exam created");
      setShowAdd(false);
      setForm({ title: "", description: "", duration_minutes: 10, passing_percent: 40 });
      setQuestions([emptyQuestion()]);
      fetchExams();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create exam");
    }
  };

  const setStatus = async (id, status) => {
    await axios.patch(`${API}/exams/${id}`, { status }, { withCredentials: true });
    fetchExams();
  };

  const deleteExam = async (id) => {
    await axios.delete(`${API}/exams/${id}`, { withCredentials: true });
    toast.success("Exam deleted");
    fetchExams();
  };

  return (
    <div data-testid="exams-section">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2">
          <Button variant={tab === "exams" ? "default" : "outline"} size="sm" data-testid="tab-exams" onClick={() => setTab("exams")}>
            Exams ({exams.length})
          </Button>
          <Button variant={tab === "results" ? "default" : "outline"} size="sm" data-testid="tab-results" onClick={() => setTab("results")}>
            Results ({attempts.length})
          </Button>
        </div>
        {tab === "exams" && (
          <Button size="sm" className="gap-1" data-testid="add-exam-button" onClick={() => setShowAdd(true)}>
            <Plus className="h-4 w-4" /> Create Exam
          </Button>
        )}
      </div>

      {tab === "exams" ? (
        <div className="mt-6 overflow-x-auto rounded-2xl border border-border bg-card shadow-sm">
          <Table data-testid="exams-table">
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Questions</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Passing</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {exams.map((exam) => (
                <TableRow key={exam.id} data-testid={`exam-row-${exam.id}`}>
                  <TableCell className="font-medium">{exam.title}</TableCell>
                  <TableCell>{exam.questions.length}</TableCell>
                  <TableCell>{exam.duration_minutes} min</TableCell>
                  <TableCell>{exam.passing_percent}%</TableCell>
                  <TableCell>
                    <Select value={exam.status} onValueChange={(v) => setStatus(exam.id, v)}>
                      <SelectTrigger className="w-28" data-testid={`exam-status-${exam.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">active</SelectItem>
                        <SelectItem value="draft">draft</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Button variant="ghost" size="icon" data-testid={`delete-exam-${exam.id}`} onClick={() => deleteExam(exam.id)} aria-label="Delete exam">
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="mt-6 overflow-x-auto rounded-2xl border border-border bg-card shadow-sm">
          <Table data-testid="attempts-table">
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Candidate</TableHead>
                <TableHead>Exam</TableHead>
                <TableHead>Score</TableHead>
                <TableHead>Result</TableHead>
                <TableHead>Certificate</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {attempts.map((a) => (
                <TableRow key={a.id} data-testid={`attempt-row-${a.id}`}>
                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                    {new Date(a.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="font-medium">
                    {a.name}
                    <span className="block text-xs text-muted-foreground">{a.phone}</span>
                  </TableCell>
                  <TableCell>{a.exam_title}</TableCell>
                  <TableCell>{a.score}/{a.total} ({a.percent}%)</TableCell>
                  <TableCell>
                    <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${a.passed ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" : "bg-red-500/15 text-red-600 dark:text-red-400"}`}>
                      {a.passed ? "PASS" : "FAIL"}
                    </span>
                  </TableCell>
                  <TableCell>
                    {a.certificate_no ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1"
                        data-testid={`cert-download-${a.id}`}
                        onClick={() => window.open(`${API}/certificates/${a.certificate_no}/pdf`, "_blank")}
                      >
                        <FileDown className="h-3.5 w-3.5" /> {a.certificate_no}
                      </Button>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {attempts.length === 0 && (
            <p className="p-8 text-center text-sm text-muted-foreground">No attempts yet.</p>
          )}
        </div>
      )}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent data-testid="add-exam-dialog" className="max-h-[90vh] max-w-2xl overflow-y-auto" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-heading">Create Online Exam</DialogTitle>
          </DialogHeader>
          <form onSubmit={createExam} className="grid gap-4" data-testid="add-exam-form">
            <div className="grid gap-2">
              <Label>Exam Title *</Label>
              <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="exam-title-input" />
            </div>
            <div className="grid gap-2">
              <Label>Description</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="exam-description-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Duration (minutes)</Label>
                <Input type="number" min="1" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: Number(e.target.value) })} data-testid="exam-duration-input" />
              </div>
              <div className="grid gap-2">
                <Label>Passing %</Label>
                <Input type="number" min="1" max="100" value={form.passing_percent} onChange={(e) => setForm({ ...form, passing_percent: Number(e.target.value) })} data-testid="exam-passing-input" />
              </div>
            </div>
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Questions</p>
            {questions.map((q, qi) => (
              <div key={q.key} className="space-y-2 rounded-xl border border-border p-4" data-testid={`exam-question-block-${qi}`}>
                <div className="flex items-center justify-between gap-2">
                  <Textarea
                    rows={2}
                    placeholder={`Question ${qi + 1}`}
                    value={q.question}
                    onChange={(e) => updateQ(qi, "question", e.target.value)}
                    data-testid={`exam-question-${qi}`}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setQuestions(questions.filter((_, j) => j !== qi))}
                    data-testid={`exam-question-remove-${qi}`}
                    aria-label="Remove question"
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {q.options.map((opt, oi) => (
                    <Input
                      key={`${q.key}-${oi}`}
                      placeholder={`Option ${oi + 1}`}
                      value={opt}
                      onChange={(e) => updateOption(qi, oi, e.target.value)}
                      data-testid={`exam-option-${qi}-${oi}`}
                    />
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs">Correct Answer</Label>
                  <Select value={String(q.correct_index)} onValueChange={(v) => updateQ(qi, "correct_index", Number(v))}>
                    <SelectTrigger className="w-36" data-testid={`exam-correct-${qi}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {q.options.map((_, oi) => (
                        <SelectItem key={`${q.key}-${oi}`} value={String(oi)}>Option {oi + 1}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" className="gap-1" onClick={() => setQuestions([...questions, emptyQuestion()])} data-testid="add-question-button">
              <Plus className="h-4 w-4" /> Add Question
            </Button>
            <Button type="submit" data-testid="exam-submit-button">Create Exam</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
